import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { Auth, getIdToken } from '@angular/fire/auth';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Customer } from '../../shared/../customer/shared/customer.model';
import { CustomerService } from '../../shared/../customer/shared/customer.service';
import { RemoveCommasPipe } from '../../shared/remove-commas.pipe';
import { CustomerNamesComponentState } from '../../shared/state-interface';
import { StateManager } from '../../shared/state-manager.class';

@Component({
    selector: 'app-customer-names-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatAutocompleteModule,
        CommonModule,
        RemoveCommasPipe
    ],
    templateUrl: './customer-names.component.html',
    styleUrl: './customer-names.component.css'
})
export class CustomerNamesComponent {

    @ViewChild("input") userInput: ElementRef<HTMLInputElement> | undefined;
    @Output() messageEvent = new EventEmitter<string>();

    private auth: Auth = inject(Auth);
    private stateManager: StateManager = inject(StateManager);
    private customerService: CustomerService = inject(CustomerService);

    private static _customerNamesError = "Customer Names error:";

    protected customer: FormControl = new FormControl<string | Customer>("");
    protected filteredCustomers: Customer[] = [];

    private _customerNames: WritableSignal<Customer[]> = signal([]);
    private _show: WritableSignal<boolean> = signal(true);

    private _invokingContext: string | undefined;
    private _dealerId: number | undefined;
    private _includeInactivated: boolean | undefined;

    public getState() {
        const state: CustomerNamesComponentState = {} as CustomerNamesComponentState;
        state.invokingContext = this._invokingContext;
        state.dealerId = this._dealerId;
        state.includeInactivated = this._includeInactivated;
        state.customerControl = this.customer;
        state.show = this._show();
        return state;
    }

    public restoreState(state: CustomerNamesComponentState) {
        this.customer = state.customerControl;
        this._customerNames.set(this.stateManager.getCustomerNames(state.invokingContext)!);
        this.init(state.invokingContext, state.dealerId, state.includeInactivated);
        this._show.set(state.show);
    }

    public init(invokingContext?: string, dealerId?: number, includeInactivated?: boolean) {
        this._invokingContext = invokingContext;
        this._dealerId = dealerId;
        this._includeInactivated = includeInactivated;

        if (this._customerNames() == undefined || this._customerNames().length == 0) {
            const customerNames = this.stateManager.getCustomerNames(this._invokingContext);

            if (customerNames == undefined) {
                this.customer.setValue("Loading...");
                this.loadCustomerNames();
            } else {
                this._customerNames.set(customerNames);
            }
        }
    }

    public get show(): boolean {
        return this._show();
    }

    public set show(value: boolean) {
        this._show.set(value);
    }

    // Note: Must use "arrow" function declaration here
    // in order to have access to 'this'. If using normal
    // function declaration, then 'this' will refer to the
    // mat-select object.
    public displayWithFunc = (customer: Customer) => {
        if (customer instanceof (Customer)) {
            return `${customer.first_name} ${customer.last_name}`;
        } else {
            return customer;
        }
    }

    public filter(): void {
        if (this.userInput != undefined && this._customerNames().length > 0) {
            const filterValue = this.userInput.nativeElement.value.toLowerCase();
            this.filteredCustomers = this._customerNames().filter(
                customer =>
                    `${customer.id} ${customer.first_name} ${customer.last_name}`.toLowerCase().includes(filterValue)
            );
        }
    }

    public get customerCtl(): FormControl {
        return this.customer;
    }

    public get selectedCustomer(): number {
        return this.customer.value instanceof Customer ? this.customer.value.id : -1;
    }

    public get customerNames(): Customer[] {
        return this._customerNames();
    }

    protected async loadCustomerNames() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.customerService.getCustomerNames(await getIdToken(currentUser),
                                            this._dealerId, this._includeInactivated);

            observable.subscribe(response => {
                // For testing to validate proper rendering of form fields
                // when retrieving data over slow internet connections.
                //setTimeout(() => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-customer-names"] == "success") {
                        // this.customerNames is a signal. The view will be refreshed
                        // upon update to the signal.
                        this._customerNames.set(Customer.toCustomers(json["customer-names"]));
                        this.stateManager.setCustomerNames(this._customerNames(), this._invokingContext);
                        this.customer.setValue("");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${CustomerNamesComponent._customerNamesError} ${(<Error>error).message}`;
                    console.error(message);
                    this.messageEvent.emit(message);
                }
                //}, 2500);
            })
        }
    }
}